import React, { useState } from "react";
import { useConfirm } from "material-ui-confirm";
import { useDispatch, useSelector } from "react-redux";
import {
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from "@mui/material";
import Chart from "react-google-charts";
import { useBeforeunload } from "react-beforeunload";

import GameHeader from "../GameHeader";
import GameResults from "../GameResults";

import { handleExitGame } from "../../../helpers/handleExitGame";
import { regions, regionCodes } from "../../../data/index.js";

const Maps = ({ game }) => {
  useBeforeunload((e) => e.preventDefault());
  const confirm = useConfirm();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [regionView, setRegionView] = useState(game.region);
  const [foundCountries, setFoundCountries] = useState([]);
  const [confirmCountry, setConfirmCountry] = useState(null);
  const [forceRender, setForceRender] = useState(false);

  const [score, setScore] = useState(0);
  const [progress, setProgress] = useState(1);
  let countryData = useSelector((state) => state.game.game.countryData);

  let rowData = countryData.map((country) => {
    return [country.code, 0];
  });

  const mapData = [["Country", "Found"], ...rowData];

  const [data, setData] = useState(mapData);

  const getSelectedCountry = (chartWrapper) => {
    const chart = chartWrapper.getChart();
    const selection = chart.getSelection();
    if (selection.length === 0) return;
    const region = data[selection[0].row + 1][0];
    return region;
  };

  const confirmSelectedCountry = (selectedCountry, answerCountry) => {
    if (foundCountries.includes(selectedCountry)) {
      return;
    }

    if (!confirmCountry || confirmCountry !== selectedCountry) {
      let updatedData = data.map((country) => {
        if (country[1] === 3) {
          return [country[0], 0];
        }
        if (country[0] === selectedCountry) {
          return [country[0], 3];
        }
        return country;
      });
      setData(updatedData);
      setConfirmCountry(selectedCountry);
      return;
    }

    if (confirmCountry) {
      updateData(selectedCountry, answerCountry);
      return;
    }
  };

  const updateData = (selectedCountry, answerCountry) => {
    let correct = selectedCountry === answerCountry;

    let updatedData = data.map((country) => {
      if (country[0] === answerCountry) {
        setConfirmCountry(null);
        return [country[0], correct ? 1 : 2];
      }
      if (country[1] === 3) {
        return [country[0], 0];
      }

      return country;
    });
    if (correct) {
      setScore(score + 1);
    }
    setData(updatedData);
    setConfirmCountry(null);
    setFoundCountries([...foundCountries, answerCountry]);
    setQuestionNumber(questionNumber + 1);
    if (progress >= countryData.length) {
      setOpen(true);
      return;
    }
    setProgress(progress + 1);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: "0.5rem",
          width: "100%",
          maxWidth: "1000px",
          margin: "0.5rem auto",
        }}
      >
        <GameHeader
          score={score}
          progress={progress}
          totalQuestions={countryData.length}
          handleExitGame={() => {
            handleExitGame(confirm, dispatch);
          }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: `${
              window.innerWidth < 1000 ? "space-evenly" : "center"
            }`,
            alignItems: "center",
            gap: "0.5rem",
            width: "100%",
          }}
        >
          <Typography
            variant={`${window.innerWidth < 1000 ? "h6" : "h5"}`}
            sx={{ textAlign: "center" }}
          >
            {countryData[questionNumber]?.name}
          </Typography>
          <FormControl size="small" sx={{ width: { xs: "35%", md: "15%" } }}>
            <InputLabel id="region-select">
              <Typography variant="overline">Region</Typography>
            </InputLabel>
            <Select
              id="region-select"
              value={regionView}
              onChange={(e) => {
                setRegionView(e.target.value);
              }}
              label={<Typography variant="overline">Region</Typography>}
              MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
            >
              {Object.keys(regionCodes)
                .filter((region) => {
                  if (game.region === "World") {
                    return true;
                  }
                  return region.includes(
                    game.region.slice(0, game.region.length - 1)
                  );
                })
                .map((region, idx) => {
                  return (
                    <MenuItem
                      value={region}
                      key={idx + 1}
                      sx={{
                        fontWeight: `${
                          regions.includes(region) ? "700" : "400"
                        }`,
                      }}
                    >
                      {region}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          width: "90%",
          overflow: "scroll",
          margin: "auto",
          backgroundColor: "transparent",
          borderRadius: "10px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          maxHeight: "70vh",
        }}
      >
        <Chart
          chartEvents={[
            {
              eventName: "select",
              callback: ({ chartWrapper }) => {
                let selectedCountry = getSelectedCountry(chartWrapper);
                let answerCountry = countryData[questionNumber].code;

                confirmSelectedCountry(selectedCountry, answerCountry);
              },
            },
            {
              eventName: "ready",
              callback: () => {
                if (!forceRender) {
                  setForceRender(true);
                }
              },
            },
          ]}
          chartType="GeoChart"
          style={{
            minWidth: "1000px",
            overflow: "scroll",
            margin: "auto",
          }}
          data={data}
          options={{
            backgroundColor: {
              fill: "#79bee8",
            },
            legend: "none",
            colorAxis: {
              colors: ["#F7FBFE", "#729601", "#da121a", "#fdcb58"],
              values: [0, 1, 2, 3],
            },
            datalessRegionColor: "#3f493b",
            defaultColor: "#F7FBFE",
            region: `${regionCodes[regionView]}`,
            tooltip: { trigger: "none" },
          }}
        />
      </Paper>
      <GameResults
        open={open}
        setOpen={setOpen}
        game={{ ...game, questions: countryData.length }}
        score={score}
      />
    </>
  );
};

export default Maps;
